import React, { useRef } from 'react';
import { Sparkles, Shield, Zap, TrendingUp } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { coreValues } from '../mock/data';

const iconComponents = {
  Sparkles,
  Shield,
  Zap,
  TrendingUp
};

const CoreValues = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const gradients = [
    'from-pink-500 via-purple-500 to-indigo-500',
    'from-cyan-500 via-blue-500 to-purple-500',
    'from-yellow-500 via-orange-500 to-red-500',
    'from-green-500 via-emerald-500 to-teal-500'
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgba(139, 92, 246, 0.3) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            SIMPLE, SECURE, SWIFT &{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400">
              SCALABLE
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base sm:text-lg md:text-xl text-gray-300 max-w-4xl mx-auto font-light text-justify hyphens-auto"
          >
            Our core values drive everything we do, ensuring the best experience for our customers
          </motion.p>
        </motion.div>

        {/* Values Grid with 3D cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {coreValues.map((value, index) => {
            const IconComponent = iconComponents[value.icon];
            return (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 50, rotateX: -20 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  rotateY: 5,
                  boxShadow: '0 40px 80px rgba(139, 92, 246, 0.4)'
                }}
                className="group relative bg-white/5 backdrop-blur-xl p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-white/10 transition-all duration-500 overflow-hidden text-left"
                style={{ perspective: '1000px' }}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${gradients[index]} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}
                />

                {/* Logo + main heading row */}
                <div className="flex items-start gap-3 sm:gap-4 mb-4 min-w-0">
                  <motion.div
                    whileHover={{ rotateY: 180, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className={`relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br ${gradients[index]} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl`}
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <IconComponent className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
                  </motion.div>

                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                    transition={{ duration: 0.35, delay: 0.25 + index * 0.08 }}
                    className="text-lg sm:text-xl md:text-2xl font-bold text-white flex-1 min-w-0 leading-tight tracking-tight"
                    style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                  >
                    {value.title}
                  </motion.h3>
                </div>

                <h4 className="text-xs sm:text-sm font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent mb-3 sm:mb-4 uppercase tracking-wider hyphens-auto text-left block">
                  {value.subtitle}
                </h4>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-sm text-left hyphens-auto">{value.description}</p>

                <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-xl pointer-events-none" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CoreValues;